import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Sparkle, Plus, Trash, UploadSimple, FloppyDisk, Eye, ArrowLeft, ShieldCheck, MapPinLine, Image as ImageIcon, FilmSlate, Star, Clock } from "@phosphor-icons/react";
import { PageLoader, SmartImage } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import api, { API, TOKEN_KEY, apiErr } from "../../lib/api";
import { useConfig } from "../../lib/useConfig";

const TYPES = ["Early Bird", "Regular", "VIP", "Group"];
const MODES = [["public", "Public"], ["rsvp", "RSVP"], ["approval", "Approval required"], ["invite_only", "Invite only"]];
const PRESETS = [
  "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1200&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1200&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1521424159246-e4a66f267e4b?w=1200&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1603646315107-acbf16a28c6e?w=1200&q=80&auto=format&fit=crop",
];

const uid = (p) => p + Math.random().toString(36).slice(2, 10);
const newCat = (cpIds) => ({ id: uid("cat_"), name: "", type: "Regular", price: 0, quantity: 100, per_user_limit: 6, per_txn_limit: 6, low_stock_threshold: 15, waitlist_enabled: false, checkpoint_ids: [...cpIds] });
const newOcc = (cpIds) => ({ id: uid("occ_"), city: "", venue: "", map_link: "", date: "", time: "19:00", mode: "public", requires_payment: true, is_free: false, ticket_categories: [newCat(cpIds)] });
const blank = () => {
  const cp = { id: uid("cp_"), name: "Entry" };
  return { title: "", description: "", banner: "", genre: "", age_restriction: "All ages", terms: "",
    highlights: [], tags: [], images: [], gallery: [], deadlines: {}, checkpoints: [cp], occurrences: [newOcc([cp.id])] };
};

const DEADLINE_FIELDS = [
  ["tickets", "Ticket sales close"],
  ["rsvp", "RSVP response deadline"],
  ["invite", "Invite-only confirmation deadline"],
  ["approval", "Approval request deadline"],
];

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API}/media/upload`, {
    method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` }, body: fd,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Upload failed");
  return data;
}

function MediaManager({ gallery, banner, onChange, onCover }) {
  const [busy, setBusy] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const inputRef = useRef();
  const pick = () => inputRef.current?.click();
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of files) {
        try { const m = await uploadFile(f); uploaded.push({ id: m.id, url: m.url, kind: m.kind }); }
        catch (err) { toast.error(`${f.name}: ${err.message}`); }
      }
      if (uploaded.length) {
        const next = [...(gallery || []), ...uploaded];
        onChange(next);
        if (!banner && uploaded[0].kind === "image") onCover(uploaded[0].url);
        toast.success(`${uploaded.length} file(s) uploaded.`);
      }
    } finally { setBusy(false); e.target.value = ""; }
  };
  const remove = (i) => onChange((gallery || []).filter((_, j) => j !== i));
  const move = (i, dir) => {
    const arr = [...(gallery || [])]; const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]]; onChange(arr);
  };
  const smartOrder = async () => {
    setPolishing(true);
    try {
      const { data } = await api.post("/media/smart-order", { items: gallery });
      if (data.changed) { onChange(data.items); toast.success("AI reordered your gallery — best images first."); }
      else toast.info("Your gallery order already looks great.");
    } catch (e) { toast.error(apiErr(e)); } finally { setPolishing(false); }
  };
  const aiCrop = async (i) => {
    const m = (gallery || [])[i];
    if (!m?.id || m.kind !== "image") return;
    setPolishing(true);
    try {
      const { data } = await api.post(`/media/${m.id}/smart-crop`, { aspect: "16:9" });
      const wasCover = banner === m.url;
      const arr = [...gallery];
      arr[i] = { id: data.id, url: data.url, kind: "image" };
      onChange(arr);
      if (wasCover) onCover(data.url);
      toast.success(data.ai ? "AI cropped to a clean 16:9 cover." : "Cropped to 16:9.");
    } catch (e) { toast.error(apiErr(e)); } finally { setPolishing(false); }
  };
  return (
    <div>
      <input ref={inputRef} type="file" accept="image/*,video/*" multiple hidden onChange={onFiles} data-testid="media-input" />
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={pick} disabled={busy} className="rounded-full" data-testid="upload-media-btn">
          <UploadSimple size={14} className="mr-1" /> {busy ? "Uploading…" : "Upload images / videos"}
        </Button>
        {(gallery || []).filter((m) => m.kind === "image").length >= 2 && (
          <Button size="sm" variant="outline" onClick={smartOrder} disabled={busy || polishing} className="rounded-full" data-testid="smart-order-btn">
            <Sparkle size={14} className="mr-1" weight="fill" /> {polishing ? "AI polishing…" : "AI smart order"}
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-2">From your device · multiple files · JPG/PNG/WEBP or MP4/WEBM/MOV. AI checks images before publishing. Hover an image for one-tap AI crop.</p>
      {(gallery || []).length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
          {gallery.map((m, i) => (
            <div key={m.id || i} className="relative group rounded-lg overflow-hidden border border-border aspect-video bg-muted" data-testid={`media-${i}`}>
              {m.kind === "video"
                ? <video src={`${API.replace('/api','')}${m.url}`} className="w-full h-full object-cover" muted />
                : <img src={`${API.replace('/api','')}${m.url}`} alt="" className="w-full h-full object-cover" />}
              {banner === m.url && <span className="absolute top-1 left-1 text-[10px] bg-primary text-white px-1.5 py-0.5 rounded-full flex items-center gap-0.5"><Star size={9} weight="fill" /> Cover</span>}
              {m.kind === "video" && <span className="absolute top-1 left-1 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded-full"><FilmSlate size={10} /></span>}
              <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => move(i, -1)} className="text-white text-xs px-1.5" title="Move left">‹</button>
                {m.kind === "image" && <button onClick={() => onCover(m.url)} className="text-white text-[10px] px-1" data-testid={`set-cover-${i}`}>Cover</button>}
                {m.kind === "image" && <button onClick={() => aiCrop(i)} className="text-white px-1" title="AI crop to 16:9" data-testid={`ai-crop-${i}`}><Sparkle size={11} weight="fill" /></button>}
                <button onClick={() => remove(i)} className="text-white px-1.5" data-testid={`del-media-${i}`}><Trash size={12} /></button>
                <button onClick={() => move(i, 1)} className="text-white text-xs px-1.5" title="Move right">›</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EventEditor() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { cities, genres } = useConfig();
  const [ev, setEv] = useState(id ? null : blank());
  const [showAI, setShowAI] = useState(params.get("ai") === "1");
  const [prompt, setPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) api.get(`/organizer/events/${id}`).then((r) => setEv(r.data)).catch(() => toast.error("Event not found"));
  }, [id]);

  const set = (k, v) => setEv((e) => ({ ...e, [k]: v }));

  // checkpoints
  const cpIds = (ev?.checkpoints || []).map((c) => c.id);
  const setCp = (i, name) => setEv((e) => { const cps = [...e.checkpoints]; cps[i] = { ...cps[i], name }; return { ...e, checkpoints: cps }; });
  const addCp = () => setEv((e) => ({ ...e, checkpoints: [...e.checkpoints, { id: uid("cp_"), name: "" }] }));
  const removeCp = (i) => setEv((e) => {
    const cid = e.checkpoints[i].id;
    return { ...e, checkpoints: e.checkpoints.filter((_, j) => j !== i),
      occurrences: e.occurrences.map((o) => ({ ...o, ticket_categories: o.ticket_categories.map((c) => ({ ...c, checkpoint_ids: (c.checkpoint_ids || []).filter((x) => x !== cid) })) })) };
  });

  // occurrences
  const setOcc = (oi, patch) => setEv((e) => { const occ = [...e.occurrences]; occ[oi] = { ...occ[oi], ...patch }; return { ...e, occurrences: occ }; });
  const addOcc = () => setEv((e) => ({ ...e, occurrences: [...e.occurrences, newOcc(e.checkpoints.map((c) => c.id))] }));
  const removeOcc = (oi) => setEv((e) => ({ ...e, occurrences: e.occurrences.filter((_, j) => j !== oi) }));

  // categories
  const setCat = (oi, ci, patch) => setEv((e) => { const occ = [...e.occurrences]; const cats = [...occ[oi].ticket_categories]; cats[ci] = { ...cats[ci], ...patch }; occ[oi] = { ...occ[oi], ticket_categories: cats }; return { ...e, occurrences: occ }; });
  const addCat = (oi) => setEv((e) => { const occ = [...e.occurrences]; occ[oi] = { ...occ[oi], ticket_categories: [...occ[oi].ticket_categories, newCat(e.checkpoints.map((c) => c.id))] }; return { ...e, occurrences: occ }; });
  const removeCat = (oi, ci) => setEv((e) => { const occ = [...e.occurrences]; occ[oi] = { ...occ[oi], ticket_categories: occ[oi].ticket_categories.filter((_, j) => j !== ci) }; return { ...e, occurrences: occ }; });
  const toggleCp = (oi, ci, cid) => { const cur = ev.occurrences[oi].ticket_categories[ci].checkpoint_ids || []; setCat(oi, ci, { checkpoint_ids: cur.includes(cid) ? cur.filter((x) => x !== cid) : [...cur, cid] }); };

  const aiGenerate = async () => {
    if (!prompt.trim()) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/organizer/event-draft", { prompt });
      toast.success("AI draft created! Review and edit below.");
      if (data.missing_fields?.length) toast.info("Add: " + data.missing_fields.join(", "));
      navigate(`/organizer/events/${data.event.id}/edit`);
      setEv(data.event); setShowAI(false);
    } catch (e) { toast.error(apiErr(e)); } finally { setAiBusy(false); }
  };

  const payload = () => ({
    ...ev,
    highlights: Array.isArray(ev.highlights) ? ev.highlights : String(ev.highlights || "").split(",").map((s) => s.trim()).filter(Boolean),
    tags: Array.isArray(ev.tags) ? ev.tags : String(ev.tags || "").split(",").map((s) => s.trim()).filter(Boolean),
  });

  const save = async () => {
    setSaving(true);
    try {
      if (id) { await api.put(`/organizer/events/${id}`, payload()); toast.success("Saved."); }
      else { const { data } = await api.post("/organizer/events", payload()); toast.success("Draft created."); navigate(`/organizer/events/${data.id}/edit`); }
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const publish = async () => {
    setSaving(true);
    try { await api.put(`/organizer/events/${id}`, payload()); const { data } = await api.post(`/organizer/events/${id}/publish`); toast.success(data.message); navigate("/organizer/events"); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  if (!ev) return <PageLoader />;

  return (
    <div className="max-w-3xl">
      <button onClick={() => navigate("/organizer/events")} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft size={16} /> My Events</button>
      <h1 className="font-heading text-3xl font-semibold tracking-tight mb-1">{id ? "Edit event" : "Create event"}</h1>
      <p className="text-muted-foreground mb-6">Add multiple dates/cities as occurrences. Publishing requires admin approval.</p>

      {!id && (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-5 mb-6">
          <button onClick={() => setShowAI((s) => !s)} className="flex items-center gap-2 font-heading font-semibold" data-testid="toggle-ai-create">
            <Sparkle weight="fill" className="text-primary" /> Create with AI {showAI ? "▲" : "▼"}
          </button>
          {showAI && (
            <div className="mt-4">
              <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2} data-testid="ai-prompt"
                placeholder='e.g. "A Gujarati comedy tour in Ahmedabad and Mumbai next weekend for 800 people"' />
              <Button onClick={aiGenerate} disabled={aiBusy} className="rounded-full mt-3" data-testid="ai-generate">
                {aiBusy ? "Generating…" : <><Sparkle weight="fill" className="mr-1.5" /> Generate draft</>}
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="space-y-5">
        <div className="rounded-xl border border-border bg-card p-5">
          <Label className="mb-2 block flex items-center gap-2"><ImageIcon size={18} className="text-primary" /> Event media (images & videos)</Label>
          {ev.banner && <div className="mb-3 max-w-md"><SmartImage src={ev.banner.startsWith("http") ? ev.banner : `${API.replace('/api','')}${ev.banner}`} alt="cover" ratio="video" /></div>}
          <MediaManager gallery={ev.gallery} banner={ev.banner}
            onChange={(g) => set("gallery", g)} onCover={(url) => set("banner", url)} />
          <div className="mt-4">
            <Label className="mb-1.5 block text-xs">Or paste a cover image URL</Label>
            <Input value={ev.banner && ev.banner.startsWith("http") ? ev.banner : ""} onChange={(e) => set("banner", e.target.value)} placeholder="https://…" data-testid="ev-banner" />
            <div className="flex gap-2 mt-2">
              {PRESETS.map((p) => <button key={p} onClick={() => set("banner", p)} className="w-16 h-10 rounded overflow-hidden border border-border btn-press"><img src={p} alt="" className="w-full h-full object-cover" /></button>)}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <Label className="mb-1 block flex items-center gap-2"><Clock size={18} className="text-primary" /> Closing deadlines (optional)</Label>
          <p className="text-xs text-muted-foreground mb-3">After a deadline passes, that action closes for customers/invitees; confirmed bookings are preserved.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {DEADLINE_FIELDS.map(([k, label]) => (
              <div key={k}>
                <Label className="mb-1 block text-xs">{label}</Label>
                <Input type="datetime-local" value={(ev.deadlines || {})[k] ? String(ev.deadlines[k]).slice(0, 16) : ""}
                  onChange={(e) => set("deadlines", { ...(ev.deadlines || {}), [k]: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                  data-testid={`deadline-${k}`} />
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-5 space-y-4">
          <div><Label className="mb-1.5 block">Title</Label><Input value={ev.title} onChange={(e) => set("title", e.target.value)} data-testid="ev-title" /></div>
          <div><Label className="mb-1.5 block">Description</Label><Textarea value={ev.description} onChange={(e) => set("description", e.target.value)} rows={4} data-testid="ev-desc" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="mb-1.5 block">Genre</Label>
              <Select value={ev.genre || ""} onValueChange={(v) => set("genre", v)}><SelectTrigger data-testid="ev-genre"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{genres.map((g) => <SelectItem key={g.id} value={g.name}>{g.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="mb-1.5 block">Age restriction</Label><Input value={ev.age_restriction || ""} onChange={(e) => set("age_restriction", e.target.value)} /></div>
          </div>
        </div>

        {/* checkpoints */}
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-3">
            <Label className="flex items-center gap-2"><ShieldCheck size={18} className="text-primary" /> Checkpoints (access gates)</Label>
            <Button size="sm" variant="outline" onClick={addCp} data-testid="add-checkpoint"><Plus size={14} className="mr-1" /> Add</Button>
          </div>
          <p className="text-xs text-muted-foreground mb-3">e.g. Entry, Dinner, VIP Lounge. Assign each ticket category to the checkpoints it can access.</p>
          {ev.checkpoints.map((c, i) => (
            <div key={c.id} className="flex gap-2 mb-2">
              <Input value={c.name} onChange={(e) => setCp(i, e.target.value)} placeholder="Checkpoint name" data-testid={`checkpoint-${i}`} />
              {ev.checkpoints.length > 1 && <button onClick={() => removeCp(i)} className="text-destructive px-2"><Trash size={16} /></button>}
            </div>
          ))}
        </div>

        {/* occurrences */}
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-3">
            <Label className="flex items-center gap-2"><MapPinLine size={18} className="text-primary" /> Occurrences (date · venue · city)</Label>
            <Button size="sm" variant="outline" onClick={addOcc} data-testid="add-occurrence"><Plus size={14} className="mr-1" /> Add occurrence</Button>
          </div>

          {ev.occurrences.map((o, oi) => (
            <div key={o.id} className="rounded-lg border border-border p-4 mb-4" data-testid={`occurrence-block-${oi}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold text-sm">Occurrence {oi + 1}</span>
                {ev.occurrences.length > 1 && <button onClick={() => removeOcc(oi)} className="text-destructive text-sm inline-flex items-center gap-1"><Trash size={14} /> Remove</button>}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="mb-1 block text-xs">City</Label>
                  <Select value={o.city || ""} onValueChange={(v) => setOcc(oi, { city: v })}><SelectTrigger data-testid={`occ-city-${oi}`}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{cities.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label className="mb-1 block text-xs">Venue</Label><Input value={o.venue || ""} onChange={(e) => setOcc(oi, { venue: e.target.value })} data-testid={`occ-venue-${oi}`} /></div>
                <div className="sm:col-span-2"><Label className="mb-1 block text-xs">Google Maps link (optional)</Label><Input value={o.map_link || ""} onChange={(e) => setOcc(oi, { map_link: e.target.value })} placeholder="Paste a Google Maps link — or leave blank to auto-generate from venue & city" data-testid={`occ-map-${oi}`} /></div>
                <div><Label className="mb-1 block text-xs">Date</Label><Input type="date" value={o.date || ""} onChange={(e) => setOcc(oi, { date: e.target.value })} data-testid={`occ-date-${oi}`} /></div>
                <div><Label className="mb-1 block text-xs">Time</Label><Input type="time" value={o.time || ""} onChange={(e) => setOcc(oi, { time: e.target.value })} /></div>
                <div><Label className="mb-1 block text-xs">Mode</Label>
                  <Select value={o.mode} onValueChange={(v) => setOcc(oi, { mode: v })}><SelectTrigger data-testid={`occ-mode-${oi}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{MODES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="flex items-end gap-4 pb-1">
                  <label className="flex items-center gap-2 text-sm"><Switch checked={o.is_free} onCheckedChange={(v) => setOcc(oi, { is_free: v })} /> Free</label>
                  {o.mode !== "public" && !o.is_free && (
                    <label className="flex items-center gap-2 text-sm"><Switch checked={o.requires_payment} onCheckedChange={(v) => setOcc(oi, { requires_payment: v })} /> Paid</label>
                  )}
                </div>
              </div>

              {/* categories */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs">Ticket categories</Label>
                  <Button size="sm" variant="ghost" onClick={() => addCat(oi)} data-testid={`add-category-${oi}`}><Plus size={13} className="mr-1" /> Add</Button>
                </div>
                {o.ticket_categories.map((c, ci) => (
                  <div key={c.id} className="rounded-lg bg-muted/40 p-3 mb-2 space-y-2" data-testid={`cat-block-${oi}-${ci}`}>
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <Input className="col-span-4" placeholder="Name" value={c.name} onChange={(e) => setCat(oi, ci, { name: e.target.value })} data-testid={`cat-name-${oi}-${ci}`} />
                      <div className="col-span-3">
                        <Select value={c.type} onValueChange={(v) => setCat(oi, ci, { type: v })}><SelectTrigger className="h-10"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
                      </div>
                      <Input className="col-span-2" type="number" placeholder="₹" value={c.price} onChange={(e) => setCat(oi, ci, { price: +e.target.value })} data-testid={`cat-price-${oi}-${ci}`} />
                      <Input className="col-span-2" type="number" placeholder="Qty" value={c.quantity} onChange={(e) => setCat(oi, ci, { quantity: +e.target.value })} data-testid={`cat-qty-${oi}-${ci}`} />
                      {o.ticket_categories.length > 1 && <button onClick={() => removeCat(oi, ci)} className="col-span-1 text-destructive"><Trash size={15} /></button>}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div><Label className="text-[10px] block">Max/user</Label><Input type="number" value={c.per_user_limit} onChange={(e) => setCat(oi, ci, { per_user_limit: +e.target.value })} className="h-8" /></div>
                      <div><Label className="text-[10px] block">Max/txn</Label><Input type="number" value={c.per_txn_limit} onChange={(e) => setCat(oi, ci, { per_txn_limit: +e.target.value })} className="h-8" /></div>
                      <div><Label className="text-[10px] block">Low-stock at</Label><Input type="number" value={c.low_stock_threshold} onChange={(e) => setCat(oi, ci, { low_stock_threshold: +e.target.value })} className="h-8" /></div>
                    </div>
                    <label className="flex items-center gap-2 text-xs"><Switch checked={c.waitlist_enabled} onCheckedChange={(v) => setCat(oi, ci, { waitlist_enabled: v })} /> Enable waitlist when sold out</label>
                    {ev.checkpoints.length > 1 && (
                      <div>
                        <Label className="text-[10px] block mb-1">Checkpoint access</Label>
                        <div className="flex flex-wrap gap-1.5">
                          {ev.checkpoints.map((cp) => {
                            const on = (c.checkpoint_ids || []).includes(cp.id);
                            return <button key={cp.id} type="button" onClick={() => toggleCp(oi, ci, cp.id)} data-testid={`cp-toggle-${oi}-${ci}-${cp.id}`}
                              className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${on ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-background text-muted-foreground border-border"}`}>{cp.name || "…"}</button>;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-border bg-card p-5 space-y-4">
          <div><Label className="mb-1.5 block">Highlights (comma separated)</Label><Input value={Array.isArray(ev.highlights) ? ev.highlights.join(", ") : ev.highlights} onChange={(e) => set("highlights", e.target.value.split(","))} /></div>
          <div><Label className="mb-1.5 block">Tags (comma separated)</Label><Input value={Array.isArray(ev.tags) ? ev.tags.join(", ") : ev.tags} onChange={(e) => set("tags", e.target.value.split(","))} /></div>
          <div><Label className="mb-1.5 block">Terms</Label><Textarea value={ev.terms || ""} onChange={(e) => set("terms", e.target.value)} rows={2} /></div>
        </div>
      </div>

      <div className="sticky bottom-0 -mx-4 md:mx-0 mt-6 p-4 glass border-t border-border flex gap-2 justify-end">
        <Button variant="outline" onClick={save} disabled={saving} className="rounded-full" data-testid="save-draft"><FloppyDisk className="mr-1.5" /> Save draft</Button>
        {id && <Button variant="outline" asChild className="rounded-full"><a href={`/events/${id}`} target="_blank" rel="noreferrer"><Eye className="mr-1.5" /> Preview</a></Button>}
        {id && <Button onClick={publish} disabled={saving} className="rounded-full" data-testid="publish-event"><UploadSimple className="mr-1.5" /> Publish</Button>}
      </div>
    </div>
  );
}
